import React, { useState } from 'react';

const API_BASE_URL = 'https://ufilm-upload-api.ott-f87.workers.dev';

const UploaderComponent = () => {
  const [uploadState, setUploadState] = useState('idle');
  const [currentVideo, setCurrentVideo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [metadata, setMetadata] = useState({
    title: '',
    description: '',
    category: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['video/mp4', 'video/quicktime'].includes(file.type)) {
      alert('Only MP4 and MOV files are supported');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      alert('File size must be under 100 MB');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }
    setUploadState('uploading');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('creatorId', 'demo');
      const response = await fetch(`${API_BASE_URL}/api/upload/direct`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setCurrentVideo(data);
        setUploadState('processing');
        pollProcessingStatus(data.streamUid);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
      setUploadState('idle');
    }
  };

  const pollProcessingStatus = async (streamUid) => {
    const maxAttempts = 60;
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const response = await fetch(`${API_BASE_URL}/api/upload/status/${streamUid}`);
        const data = await response.json();
        if (data.status === 'ready') {
          clearInterval(poll);
          setCurrentVideo(data);
          setUploadState('metadata');
        } else if (data.status === 'failed') {
          clearInterval(poll);
          alert('Video processing failed');
          setUploadState('idle');
        }
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          alert('Processing timeout');
          setUploadState('idle');
        }
      } catch (error) {
        console.error('Status poll error:', error);
      }
    }, 5000);
  };

  const handleAddTag = () => {
    if (!tagInput.trim()) return;
    let tag = tagInput.trim();
    if (!tag.startsWith('#')) {
      tag = '#' + tag;
    }
    if (metadata.tags.length >= 8) {
      alert('Maximum 8 tags allowed');
      return;
    }
    if (metadata.tags.includes(tag)) {
      alert('Tag already added');
      return;
    }
    setMetadata(prev => ({
      ...prev,
      tags: [...prev.tags, tag]
    }));
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setMetadata(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handlePublish = async () => {
    if (!metadata.title || metadata.title.length < 10) {
      alert('Title must be at least 10 characters');
      return;
    }
    if (!metadata.category) {
      alert('Please select a category');
      return;
    }
    if (metadata.tags.length < 3) {
      alert('Please add at least 3 tags');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/upload/metadata/${currentVideo.streamUid || currentVideo.videoId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...metadata,
          publishStatus: 'published'
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('Video published successfully! 🎉');
        setUploadState('published');
        setTimeout(() => resetUpload(), 3000);
      } else {
        alert('Failed to publish: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Publish error:', error);
      alert('Failed to publish video');
    }
  };

  const resetUpload = () => {
    setUploadState('idle');
    setCurrentVideo(null);
    setSelectedFile(null);
    setMetadata({
      title: '',
      description: '',
      category: '',
      tags: []
    });
    setTagInput('');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', background: 'white', borderRadius: '24px', padding: '40px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <h1 style={{ fontSize: '48px', fontWeight: 'bold', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '40px', textAlign: 'center' }}>
          🎬 UFILM Upload Dashboard
        </h1>

        {uploadState === 'idle' && (
          <div style={{ border: '3px dashed #667eea', borderRadius: '20px', padding: '80px 40px', textAlign: 'center', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
            <div style={{ fontSize: '96px', marginBottom: '20px' }}>🎬</div>
            <h2 style={{ fontSize: '32px', marginBottom: '16px', color: '#2d3748' }}>Upload Your Video</h2>
            <p style={{ color: '#718096', margin: '20px 0', fontSize: '16px' }}>MP4, MOV only • Max 100 MB • 1-5 minutes • Vertical (9:16)</p>
            <input type="file" accept="video/mp4,video/quicktime" onChange={handleFileSelect} style={{ display: 'none' }} id="fileInput" />
            <label htmlFor="fileInput" style={{ display: 'inline-block', padding: '16px 48px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', marginBottom: '20px' }}>
              📁 Browse Files
            </label>
            {selectedFile && (
              <div style={{ marginTop: '30px', padding: '20px', background: 'white', borderRadius: '12px' }}>
                <p style={{ fontSize: '16px', marginBottom: '12px', color: '#2d3748' }}><strong>Selected:</strong> {selectedFile.name}</p>
                <p style={{ fontSize: '14px', color: '#718096', marginBottom: '20px' }}>Size: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                <button onClick={handleUpload} style={{ padding: '16px 48px', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px' }}>
                  🚀 Start Upload
                </button>
              </div>
            )}
          </div>
        )}

        {uploadState === 'uploading' && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '96px', marginBottom: '30px' }}>⬆️</div>
            <h2 style={{ fontSize: '32px', marginBottom: '20px', color: '#2d3748' }}>Uploading...</h2>
            <p style={{ color: '#718096', fontSize: '16px' }}>Please wait while your video is being uploaded...</p>
          </div>
        )}

        {uploadState === 'processing' && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '96px', marginBottom: '30px' }}>⚙️</div>
            <h2 style={{ fontSize: '32px', marginBottom: '20px', color: '#2d3748' }}>Processing Video...</h2>
            <p style={{ color: '#718096', fontSize: '16px' }}>This usually takes 30-60 seconds...</p>
          </div>
        )}

        {uploadState === 'metadata' && currentVideo && (
          <div>
            <h2 style={{ fontSize: '28px', marginBottom: '30px', color: '#2d3748' }}>📝 Add Video Details</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '40px' }}>
              <div>
                <div style={{ background: '#000', borderRadius: '16px', overflow: 'hidden', marginBottom: '20px', aspectRatio: '9/16' }}>
                  {currentVideo.playbackUrl ? (
                    <iframe src={`https://customer-m033z5x04zq94w7f.cloudflarestream.com/${currentVideo.streamUid || currentVideo.videoId}/iframe`} style={{ border: 'none', width: '100%', height: '100%' }} allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowFullScreen title="Video Preview" />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white', fontSize: '64px' }}>🎬</div>
                  )}
                </div>
                <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '12px', fontSize: '13px' }}>
                  <div><strong>Stream ID:</strong> {currentVideo.streamUid || currentVideo.videoId}</div>
                  <div><strong>Status:</strong> <span style={{ color: '#48bb78' }}>✅ Ready</span></div>
                </div>
              </div>
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>Title <span style={{ color: '#f56565' }}>*</span></label>
                  <input type="text" value={metadata.title} onChange={(e) => setMetadata(prev => ({ ...prev, title: e.target.value }))} placeholder="Enter video title (min 10 characters)" maxLength={60} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px' }} />
                  <div style={{ textAlign: 'right', fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>{metadata.title.length}/60</div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>Description</label>
                  <textarea value={metadata.description} onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))} placeholder="What's your video about?" maxLength={200} rows={3} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical' }} />
                  <div style={{ textAlign: 'right', fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>{metadata.description.length}/200</div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>Category <span style={{ color: '#f56565' }}>*</span></label>
                  <select value={metadata.category} onChange={(e) => setMetadata(prev => ({ ...prev, category: e.target.value }))} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', cursor: 'pointer' }}>
                    <option value="">Select category</option>
                    <option value="FamilyDrama">FamilyDrama</option>
                    <option value="Thriller">Thriller</option>
                    <option value="Romance">Romance</option>
                    <option value="Mythology">Mythology</option>
                    <option value="Sci-Fi">Sci-Fi</option>
                    <option value="Mystery">Mystery</option>
                    <option value="Horror">Horror</option>
                    <option value="Patriotic">Patriotic</option>
                    <option value="music">Music</option>
                    <option value="Phycological">Phycological</option>
                  </select>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>Tags <span style={{ color: '#f56565' }}>*</span> (min 3, max 8)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', minHeight: '50px', marginBottom: '12px' }}>
                    {metadata.tags.map(tag => (
                      <span key={tag} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' }}>
                        {tag} <span onClick={() => handleRemoveTag(tag)} style={{ cursor: 'pointer', marginLeft: '6px' }}>×</span>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddTag()} placeholder="Add tag (press Enter)" style={{ flex: 1, padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }} />
                    <button onClick={handleAddTag} style={{ padding: '10px 24px', background: '#48bb78', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Add</button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px', marginTop: '32px' }}>
                  <button onClick={() => alert('Draft save feature coming soon!')} style={{ flex: 1, padding: '16px', background: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>💾 Save Draft</button>
                  <button onClick={handlePublish} style={{ flex: 1, padding: '16px', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>🚀 Publish Now</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {uploadState === 'published' && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '96px', marginBottom: '30px' }}>✅</div>
            <h2 style={{ fontSize: '32px', marginBottom: '20px', color: '#2d3748' }}>Video Published Successfully!</h2>
            <p style={{ color: '#718096', fontSize: '16px', marginBottom: '30px' }}>Your video is now live on UFILM</p>
            <button onClick={resetUpload} style={{ padding: '16px 48px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px' }}>Upload Another Video</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploaderComponent;
